import { Sale, SaleFileNameEnum } from '@sr-sdks/api-sdk-axios';
import { Button, Icon } from '@sr/ui-library';
import { keepPreviousData } from '@tanstack/react-query';
import { ColumnDef } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  SALES_DEFAULT_PARAMS,
  useSales,
} from '../../../../api/services/saleApi';
import { StatusBadge } from '../../../../components/Badges/StatusBadge';
import CurrencyFormatter from '../../../../components/CurrencyFormatter';
import DateFormatter from '../../../../components/DateFormatter';
import { AppDataTable } from '../../../../components/data-table';
import { LAST_SUCCESSFUL_DATA_SCOPES } from '../../../../constant/lastSuccessfulDataScopes';
import { useLastSuccessfulData } from '../../../../hooks/useLastSuccessfulData';
import { beneficiaryService } from '../../../../reducers/services/beneficiarySlice';
import { useTypedSelector } from '../../../../stateStore';
import downloadApi from '../../../../utils/api/downloadApi';

interface SalesReportRow extends Sale {
  _id: string;
}

const PAGE_SIZE = 50;

/**
 * Reusable sales report table using the new AppDataTable component.
 * Renders sales data for a title, fetched via TanStack Query (see
 * `useSales`). Preserves the exact behavior of the legacy SalesReportTable
 * component.
 */
export const SalesReportDataTable = () => {
  const currentBeneficiary = useTypedSelector(
    beneficiaryService.selectors.currentBeneficiary,
  );

  // Keyed on `${saleId}-${fileName}` so PDF and CSV buttons on the same row
  // (and across rows) each get their own independent loading spinner.
  const [downloadingKey, setDownloadingKey] = useState<null | string>(null);

  // Seeded from the URL on first render (not just `useState(1)`) so a deep
  // link like `?salesReportTablePage=3` fetches page 3 straight away instead
  // of requesting page 1 first and correcting itself once
  // DataTablePagination's mount effect reports the real URL page.
  const [searchParams] = useSearchParams();
  // Tracks the live sort so it can be passed back into AppDataTable for the
  // header's active/direction state — a hardcoded constant here would freeze
  // AppDataTable's toggle logic on its first direction forever.
  const [sortBy, setSortBy] = useState(
    () =>
      searchParams.get('salesReportTableSortBy') ?? SALES_DEFAULT_PARAMS.sortBy,
  );
  const [page, setPage] = useState(
    () =>
      Number(searchParams.get('salesReportTablePage')) ||
      SALES_DEFAULT_PARAMS.page,
  );

  const queryParams = useMemo(() => {
    if (!currentBeneficiary) return undefined;

    return {
      beneficiaryTitleId: currentBeneficiary.beneficiaryTitleId,
      page,
      pageSize: PAGE_SIZE,
      serviceId: currentBeneficiary.serviceId,
      sortBy,
    };
  }, [currentBeneficiary, page, sortBy]);

  const {
    data: saleData,
    isError,
    isLoading: isSaleLoading,
    isPlaceholderData,
  } = useSales(queryParams, {
    placeholderData: keepPreviousData,
  });

  // A failed page>1 fetch settles `saleData` to `undefined` —
  // `keepPreviousData` only covers the pending state, not error. Falling
  // back to the last successful response keeps Prev/Next usable instead of
  // stranding the user on an empty table.
  const lastGoodSaleData = useLastSuccessfulData(
    saleData,
    isError,
    LAST_SUCCESSFUL_DATA_SCOPES.SALES_REPORT,
    currentBeneficiary,
  );

  const sales = (lastGoodSaleData?.items || []) as SalesReportRow[];
  const paginationMeta = lastGoodSaleData?.meta;

  const handlePageRequest = useCallback(
    (nextPage: number, nextSortBy: string = SALES_DEFAULT_PARAMS.sortBy) => {
      setPage(nextPage);
      setSortBy(nextSortBy);
    },
    [],
  );

  const handleDownloadSalesReport = useCallback(
    async (fileName: SaleFileNameEnum, saleId: string) => {
      const key = `${saleId}-${fileName}`;

      setDownloadingKey(key);

      try {
        const saleBlob = await downloadApi.sale(fileName, saleId);
        const file = `${saleId}.${fileName}`;

        downloadApi.downloadFile(saleBlob, file);
      } catch {
        // Swallowed intentionally: download errors surface via the global ErrorModal.
      } finally {
        setDownloadingKey(null);
      }
    },
    [],
  );

  // Define columns for the AppDataTable
  const columns: ColumnDef<SalesReportRow>[] = [
    {
      accessorKey: 'saleDate',
      cell: ({ row }) =>
        row.original.saleDate && (
          <DateFormatter dateString={row.original.saleDate} />
        ),
      header: 'Date',
      meta: { sortKey: 'saleDate' },
    },
    {
      accessorKey: 'agreementName',
      cell: ({ row }) => row.original.agreementName,
      header: 'Agreement Name',
    },
    {
      accessorKey: 'amount',
      cell: ({ row }) => (
        <CurrencyFormatter amount={row.original.amount as number} />
      ),
      header: 'Amount',
    },
    {
      accessorKey: 'currency',
      cell: ({ row }) => row.original.currency,
      header: 'Currency',
    },
    {
      accessorKey: 'status',
      cell: ({ row }) => (
        <StatusBadge group="Sale" type={row.original.status} />
      ),
      header: 'Status',
    },
    {
      accessorKey: 'action',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button
            disabled={!row.original.hasSalesReportPdf}
            loading={
              downloadingKey === `${row.original._id}-${SaleFileNameEnum.Pdf}`
            }
            size="icon"
            variant="ghost"
            onClick={() =>
              handleDownloadSalesReport(SaleFileNameEnum.Pdf, row.original._id)
            }
          >
            <Icon icon="filePdf" weight="fill" />
          </Button>
          <Button
            disabled={!row.original.hasSalesReportCsv}
            loading={
              downloadingKey === `${row.original._id}-${SaleFileNameEnum.Csv}`
            }
            size="icon"
            variant="ghost"
            onClick={() =>
              handleDownloadSalesReport(SaleFileNameEnum.Csv, row.original._id)
            }
          >
            <Icon icon="fileCsv" weight="fill" />
          </Button>
        </div>
      ),
      enableSorting: false,
      header: 'Action',
    },
  ];

  return (
    <AppDataTable
      columns={columns}
      data={sales}
      emptyState={{
        description:
          'There are currently no sales reports associated with this title.',
        title: 'No Sales Reports Available',
      }}
      isFetchingPage={isPlaceholderData}
      isLoading={isSaleLoading}
      paginationKey="salesReportTablePage"
      paginationMeta={paginationMeta}
      sortBy={sortBy}
      sortParam="salesReportTableSortBy"
      onPageRequest={handlePageRequest}
    />
  );
};
